import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Layout from "../components/Layout";
import docusignService, {
  addStateDocusignService,
} from "../services/docusign.service";

// #11/#19 - Mock DocuSign page. Stands in for the real DocuSign-hosted signing
// page in every environment except production (see app/lib/docusign.js). It
// mimics the real flow exactly: the agent arrived here by a full-page redirect,
// and leaving (sign or cancel) navigates the whole tab back to the returnUrl,
// just as real DocuSign would. The `kind` param selects which signing session
// (contract vs add-state) the sign/cancel actions hit.
const MockDocusign = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl") || "/contract";
  const kind = searchParams.get("kind");
  const service = kind === "add-state" ? addStateDocusignService : docusignService;
  const [busy, setBusy] = useState(false);

  // #65 - a release signing marks the request's single release complete; otherwise
  // it's the payment/add-state signing session.
  const signAction =
    kind === "release"
      ? () => docusignService.mockReleaseSign(id)
      : () => service.mockSign(id);
  const cancelAction =
    kind === "release" ? () => Promise.resolve() : () => service.mockCancel(id);

  const title =
    kind === "release"
      ? "Sign your carrier release letter"
      : "Sign your W-9 and EFT forms";

  const leave = (action) => {
    setBusy(true);
    action()
      .then(() => {
        window.location.href = returnUrl;
      })
      .catch(() => {
        // Even on error, send them back so they're not stranded on the mock page.
        window.location.href = returnUrl;
      });
  };

  return (
    <Layout>
      <Box sx={{ padding: 4, display: "flex", justifyContent: "center" }}>
        <Paper sx={{ p: 4, maxWidth: 560, textAlign: "center" }} elevation={2}>
          <Stack spacing={3} alignItems="center">
            <Alert severity="info" sx={{ width: "100%" }}>
              This is a mock DocuSign page (non-production). No real document is
              sent or signed.
            </Alert>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              In production this would be the real DocuSign signing experience.
              Choose an action below to continue.
            </Typography>
            {busy ? (
              <CircularProgress />
            ) : (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => leave(signAction)}
                >
                  Sign
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  onClick={() => leave(cancelAction)}
                >
                  Cancel and leave
                </Button>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Box>
    </Layout>
  );
};

export default MockDocusign;
